// src/screens/Profile.tsx
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogoLoader } from "../components/LogoLoader";
import { supabase } from "../supabaseClient";
import { BadgeCheck, Lock, Settings, KeyRound, Bell, Trash2, AlertTriangle } from "lucide-react"; // 👈 KeyRound ajouté
import i18n from "../i18n";
import { storeName } from "../lib/billing";

// ---- mini hook i18n safe (même pattern que CreateTeam) ----
function useI18nSafe() {
  const [lng, setLng] = useState((i18n.resolvedLanguage || i18n.language || "fr").toLowerCase());
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string =>
    options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key));
  return { t, lng };
}

type Socials = {
  instagram?: string;
  twitter?: string;
  youtube?: string;
  tiktok?: string;
  website?: string;
};

type FavTeam = {
  id: string;
  name: string;
  league?: string | null;
  logo_path: string; // ex: "team-logos/lakers.png" ou "/logos/lakers.png"
};

export const Profile = () => {
  const { t, lng } = useI18nSafe();

  const [profile, setProfile] = useState({
    full_name: "",
    bio: "",
    photo_url: "",
    club: "",
    niveau: "",
    is_premium: false,
    achievements: "",
    socials: {} as Socials,
    pseudo: "",
    public_use_pseudo: false,
  });
  const [signedUrl, setSignedUrl] = useState<string>("");
  const [loading, setLoading] = useState(true);

  // --- Équipes préférées ---
  const [favoriteTeams, setFavoriteTeams] = useState<FavTeam[]>([]);
  const [teamLogos, setTeamLogos] = useState<Record<string, string>>({});

  // Suppression de compte (exigence App Store 5.1.1(v))
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const publicLogoUrl = (logoPath: string) => {
    if (logoPath.startsWith("/")) return logoPath;
    if (logoPath.includes("/")) {
      const [bucket, ...rest] = logoPath.split("/");
      const key = rest.join("/");
      return supabase.storage.from(bucket).getPublicUrl(key).data.publicUrl;
    }
    return supabase.storage.from("team-logos").getPublicUrl(logoPath).data.publicUrl;
  };

  const ensureProtocol = (url?: string) => {
    if (!url) return "";
    if (/^https?:\/\//i.test(url)) return url;
    return `https://${url}`;
  };

  useEffect(() => {
    const loadProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }

      // Profil (+ socials + pseudo + préférence d'affichage)
      const { data } = await supabase
        .from("user_profiles")
        .select(`
          full_name, bio, photo_url, club, niveau, is_premium, achievements,
          socials, pseudo, public_use_pseudo
        `)
        .eq("id", user.id)
        .single();

      if (data) {
        setProfile((prev) => ({
          ...prev,
          ...data,
          socials: (data.socials || {}) as Socials,
          pseudo: data.pseudo || "",
          public_use_pseudo: !!data.public_use_pseudo,
        }));
        if (data.photo_url) {
          const { data: urlData } = await supabase.storage
            .from("avatars")
            .createSignedUrl(data.photo_url, 3600);
          setSignedUrl(urlData?.signedUrl || "");
        }
      }

      // Favoris -> ids
      const { data: favs } = await supabase
        .from("user_favorite_teams")
        .select("team_id")
        .eq("user_id", user.id);

      const favIds = (favs || []).map((f: any) => String(f.team_id));
      if (favIds.length) {
        const { data: teams } = await supabase
          .from("teams_catalog")
          .select("id,name,league,logo_path")
          .in("id", favIds);

        const list = (teams || []) as FavTeam[];
        setFavoriteTeams(list);

        const entries = list.map((t) => [t.id, publicLogoUrl(t.logo_path)] as const);
        setTeamLogos(Object.fromEntries(entries));
      } else {
        setFavoriteTeams([]);
        setTeamLogos({});
      }

      setLoading(false);
    };

    loadProfile();
  }, []);

  if (loading) {
    return <LogoLoader />;
  }

  const achievementsList =
    profile.achievements?.trim()
      ? profile.achievements.split("\n").map((l) => l.trim()).filter(Boolean)
      : [];

  const hasSocials =
    !!(profile.socials?.instagram ||
      profile.socials?.twitter ||
      profile.socials?.youtube ||
      profile.socials?.tiktok ||
      profile.socials?.website);

  // Nom affiché publiquement : pseudo si activé + présent, sinon nom complet
  const displayName =
    (profile.public_use_pseudo && profile.pseudo)
      ? profile.pseudo
      : (profile.full_name || t("common:not_provided", t("common:not_provided") as any) || t("profile:not_provided"));

  // Libellé i18n (fallback si la clé n'existe pas)
  const resetLabel = i18n.exists("profile:actions.reset_password")
    ? t("profile:actions.reset_password")
    : (lng === "fr" ? "Réinitialiser le mot de passe" : "Reset password");

  const deleteWord = t("profile:danger.confirm_word");
  const canConfirmDelete =
    deleteConfirm.trim().toUpperCase() === deleteWord.toUpperCase();

  const handleDeleteAccount = async () => {
    if (!canConfirmDelete || deleting) return;
    setDeleting(true);
    setDeleteError("");
    try {
      const { error } = await supabase.functions.invoke("delete-account", {
        method: "POST",
      });
      if (error) throw error;
      await supabase.auth.signOut();
      window.location.href = "/";
    } catch {
      setDeleteError(t("profile:danger.error"));
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100 transition-colors">
      <div className="p-6 max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold mb-4 flex items-center gap-2">
          <span className="text-orange-600 dark:text-orange-400">{t("profile:title")}</span>
          {profile.is_premium && (
            <span className="text-yellow-500" title={t("profile:premium_user_title")}>
              <BadgeCheck className="w-6 h-6" />
            </span>
          )}
        </h1>

        <section className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-6 shadow-sm space-y-6">
          {/* Avatar */}
          {signedUrl && (
            <img
              src={signedUrl}
              alt={t("profile:avatar_alt")}
              className="w-32 h-32 rounded-full object-cover ring-2 ring-orange-500/20"
            />
          )}

          {/* Infos */}
          <div className="space-y-2">
            <p>
              <strong className="text-gray-700 dark:text-gray-300">{t("profile:public_name")}:</strong>{" "}
              {displayName}
            </p>
            <p>
              <strong className="text-gray-700 dark:text-gray-300">{t("profile:username")}:</strong>{" "}
              {profile.pseudo || t("profile:not_provided")}
            </p>
            <p>
              <strong className="text-gray-700 dark:text-gray-300">{t("profile:full_name")}:</strong>{" "}
              {profile.full_name || t("profile:not_provided")}
            </p>

            <p>
              <strong className="text-gray-700 dark:text-gray-300">{t("profile:club")}:</strong>{" "}
              {profile.club || t("profile:not_provided")}
            </p>
            <p>
              <strong className="text-gray-700 dark:text-gray-300">{t("profile:level")}:</strong>{" "}
              {profile.niveau || t("profile:not_provided")}
            </p>

            {achievementsList.length > 0 && (
              <div className="pt-2">
                <strong className="text-gray-700 dark:text-gray-300">{t("profile:achievements")}:</strong>
                <ul className="mt-2 list-disc list-inside space-y-1 text-gray-800 dark:text-gray-200">
                  {achievementsList.map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="pt-2">
              <strong className="text-gray-700 dark:text-gray-300">{t("profile:bio")}:</strong>
              <p className="mt-1 text-gray-800 dark:text-gray-200">
                {profile.bio || t("profile:bio_not_provided")}
              </p>
            </div>

            {/* Socials */}
            {hasSocials && (
              <div className="pt-2">
                <strong className="text-gray-700 dark:text-gray-300">{t("profile:socials_title")}:</strong>
                <div className="mt-2 flex flex-wrap gap-3 text-sm">
                  {profile.socials.instagram && (
                    <a
                      href={ensureProtocol(profile.socials.instagram)}
                      target="_blank" rel="noreferrer"
                      className="underline text-blue-600 dark:text-blue-400"
                    >
                      {t("social:instagram")}
                    </a>
                  )}
                  {profile.socials.twitter && (
                    <a
                      href={ensureProtocol(profile.socials.twitter)}
                      target="_blank" rel="noreferrer"
                      className="underline text-blue-600 dark:text-blue-400"
                    >
                      {t("social:twitter_x")}
                    </a>
                  )}
                  {profile.socials.youtube && (
                    <a
                      href={ensureProtocol(profile.socials.youtube)}
                      target="_blank" rel="noreferrer"
                      className="underline text-blue-600 dark:text-blue-400"
                    >
                      {t("social:youtube")}
                    </a>
                  )}
                  {profile.socials.tiktok && (
                    <a
                      href={ensureProtocol(profile.socials.tiktok)}
                      target="_blank" rel="noreferrer"
                      className="underline text-blue-600 dark:text-blue-400"
                    >
                      {t("social:tiktok")}
                    </a>
                  )}
                  {profile.socials.website && (
                    <a
                      href={ensureProtocol(profile.socials.website)}
                      target="_blank" rel="noreferrer"
                      className="underline text-orange-600 dark:text-orange-400"
                    >
                      {t("social:website")}
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Équipes préférées */}
          <div>
            <h2 className="text-lg font-semibold mb-2">{t("profile:favorite_teams")}</h2>
            {favoriteTeams.length === 0 ? (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t("profile:no_favorites")}{" "}
                <Link to="/edit-profile" className="text-orange-600 dark:text-orange-400 underline">
                  {t("profile:add_favorites")}
                </Link>
              </p>
            ) : (
              <div className="flex flex-wrap gap-3">
                {favoriteTeams.map((team) => (
                  <div
                    key={team.id}
                    className="flex items-center gap-2 border rounded-xl px-3 py-2 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-sm"
                    title={team.name + (team.league ? ` • ${team.league}` : "")}
                  >
                    <img
                      src={teamLogos[team.id] || ""}
                      alt={team.name}
                      className="w-7 h-7 object-contain"
                    />
                    <div className="text-sm">
                      <div className="font-medium">{team.name}</div>
                      {team.league && <div className="text-xs text-gray-500">{team.league}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            <Link
              to="/edit-profile"
              className="inline-flex items-center justify-center bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 transition-colors"
            >
              {t("profile:actions.edit_profile")}
            </Link>

            <Link
              to="/resources"
              className="inline-flex items-center justify-center bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 transition-colors"
            >
              {t("profile:actions.my_resources")}
            </Link>

            {/* 🔑 Nouveau bouton : Réinitialiser le mot de passe */}
            <Link
              to="/update-password"
              className="inline-flex items-center justify-center bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 transition-colors"
              aria-label={resetLabel}
            >
              <KeyRound className="w-5 h-5 mr-2" />
              {resetLabel}
            </Link>

            <Link
              to="/profile/notifications"
              className="inline-flex items-center justify-center bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 transition-colors"
            >
              <Bell className="w-5 h-5 mr-2" />
              {t("profile:actions.notifications")}
            </Link>

            {profile.is_premium && (
              <Link
                to="/billing/portal"
                className="inline-flex items-center justify-center bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded
                           focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 transition-colors"
              >
                <Settings className="w-5 h-5 mr-2" />
                {t("profile:actions.manage_subscription")}
              </Link>
            )}
          </div>

          {!profile.is_premium && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 p-4">
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                {t("profile:cta.premium_question")}
              </p>
              <Link
                to="/upgrade"
                className="inline-flex items-center gap-2 text-orange-600 dark:text-orange-400 underline font-medium hover:opacity-90"
              >
                <Lock className="w-4 h-4" />
                {t("profile:cta.go_premium")}
              </Link>
            </div>
          )}

          {/* Zone de danger : suppression de compte */}
          <div className="rounded-lg border border-red-300 dark:border-red-900/60 bg-red-50 dark:bg-red-950/20 p-4">
            <h2 className="text-sm font-semibold text-red-800 dark:text-red-300 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              {t("profile:danger.title")}
            </h2>
            <p className="mt-1 text-sm text-red-700/90 dark:text-red-300/80">
              {t("profile:danger.subtitle")}
            </p>
            <button
              type="button"
              onClick={() => {
                setDeleteConfirm("");
                setDeleteError("");
                setShowDelete(true);
              }}
              className="mt-3 inline-flex items-center gap-2 rounded bg-red-600 hover:bg-red-700 px-4 py-2 text-sm font-semibold text-white
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              {t("profile:danger.delete_account")}
            </button>
          </div>
        </section>
      </div>

      {showDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-account-title"
        >
          <div className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-6 shadow-xl">
            <h3
              id="delete-account-title"
              className="text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2"
            >
              <AlertTriangle className="w-5 h-5 text-red-600" />
              {t("profile:danger.modal_title")}
            </h3>
            <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
              {t("profile:danger.modal_body", { store: storeName() })}
            </p>
            <p className="mt-3 text-sm text-gray-700 dark:text-gray-300">
              {t("profile:danger.confirm_hint", { word: deleteWord })}
            </p>
            <input
              type="text"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              autoFocus
              autoCapitalize="characters"
              className="mt-2 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-3 py-2 text-sm
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
              placeholder={deleteWord}
            />

            {deleteError && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">{deleteError}</p>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowDelete(false)}
                disabled={deleting}
                className="rounded px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-60"
              >
                {t("profile:danger.cancel")}
              </button>
              <button
                type="button"
                onClick={handleDeleteAccount}
                disabled={!canConfirmDelete || deleting}
                className="inline-flex items-center gap-2 rounded bg-red-600 hover:bg-red-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" />
                {deleting ? t("profile:danger.deleting") : t("profile:danger.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
